import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ChevronDown,
  Plus,
  Search,
  Settings,
  UserPlus,
  UsersRound,
} from 'lucide-react';

interface PipelineControlBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  onOpenSettings?: () => void;
  onAddCandidate?: () => void;
  onAddExisting?: () => void;
  /** If set and both onAdd* are absent, the button renders disabled with this tooltip. */
  addDisabledReason?: string;
}

export function PipelineControlBar({
  search,
  onSearchChange,
  onOpenSettings,
  onAddCandidate,
  onAddExisting,
  addDisabledReason,
}: PipelineControlBarProps) {
  const canAdd = !!(onAddCandidate || onAddExisting);
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border/40 bg-background px-6 py-3">
        <div className="relative min-w-[180px] flex-1 max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search candidates..."
            className="h-8 pl-8 text-sm"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          {onOpenSettings && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onOpenSettings}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-hygge/10 text-hygge transition-colors hover:bg-hygge/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label="Board settings"
                >
                  <Settings className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Board settings</TooltipContent>
            </Tooltip>
          )}

          {canAdd ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="accent" size="sm">
                  <Plus className="h-3.5 w-3.5" />
                  Candidate
                  <ChevronDown className="h-3 w-3 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {onAddCandidate && (
                  <DropdownMenuItem onClick={onAddCandidate} className="cursor-pointer">
                    <UserPlus className="mr-2 h-4 w-4 text-muted-foreground" />
                    Add candidate
                  </DropdownMenuItem>
                )}
                {onAddExisting && (
                  <DropdownMenuItem onClick={onAddExisting} className="cursor-pointer">
                    <UsersRound className="mr-2 h-4 w-4 text-muted-foreground" />
                    Add existing candidate
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : addDisabledReason ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button variant="accent" size="sm" disabled>
                    <Plus className="h-3.5 w-3.5" />
                    Candidate
                    <ChevronDown className="h-3 w-3 opacity-80" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{addDisabledReason}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </TooltipProvider>
  );
}
